import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/20/solid';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { Button } from './Button';

export interface PaginationProps {
    /** 1-based */
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
}

/** At most 7 slots: first, last, the current page and its neighbours, "…" for gaps. */
export const pageSlots = (page: number, pages: number): Array<number | null> => {
    if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
    const slots = new Set([1, pages, page, page - 1, page + 1].filter((p) => p >= 1 && p <= pages));
    if (page <= 3) [2, 3, 4].forEach((p) => slots.add(p));
    if (page >= pages - 2) [pages - 1, pages - 2, pages - 3].forEach((p) => slots.add(p));
    const sorted = [...slots].sort((a, b) => a - b);
    return sorted.flatMap((p, i) => (i > 0 && p - sorted[i - 1] > 1 ? [null, p] : [p]));
};

export const Pagination = ({ page, pageSize, total, onPageChange }: PaginationProps) => {
    const { t } = useTranslation();
    const pages = Math.max(1, Math.ceil(total / pageSize));
    const from = total === 0 ? 0 : (page - 1) * pageSize + 1;
    const to = Math.min(page * pageSize, total);
    return (
        <nav aria-label={t('ui.pagination.label')} className="mt-4 flex items-center justify-between gap-3">
            <p className="text-small tabular-nums text-muted">{t('ui.pagination.range', { from, to, total })}</p>
            <div className="flex items-center gap-2">
                <Button
                    variant="outline"
                    disabled={page <= 1}
                    onClick={() => onPageChange(page - 1)}
                    icon={<ChevronLeftIcon className="size-5" aria-hidden="true" />}
                >
                    {t('ui.pagination.previous')}
                </Button>
                <ul className="hidden items-center gap-1 sm:flex">
                    {pageSlots(page, pages).map((p, i) => (
                        <li key={p ?? `gap-${i}`}>
                            {p === null ? (
                                <span aria-hidden="true" className="px-1 text-muted">
                                    …
                                </span>
                            ) : (
                                <button
                                    type="button"
                                    aria-current={p === page ? 'page' : undefined}
                                    aria-label={t('ui.pagination.page', { page: p })}
                                    onClick={() => onPageChange(p)}
                                    className={clsx(
                                        'relative size-9 rounded-control text-small tabular-nums after:absolute after:-inset-1 after:content-[""]',
                                        p === page ? 'bg-inverse text-on-inverse' : 'text-strong hover:bg-hover'
                                    )}
                                >
                                    {p}
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
                <Button
                    variant="outline"
                    disabled={page >= pages}
                    onClick={() => onPageChange(page + 1)}
                    icon={<ChevronRightIcon className="size-5 order-last" aria-hidden="true" />}
                >
                    {t('ui.pagination.next')}
                </Button>
            </div>
        </nav>
    );
};
